import React from 'react';
import Button from 'components/Button';
import moment from 'moment';
import { PDFDownloadLink } from '@react-pdf/renderer'
import NewResumeTemplate from 'components/ResumeDownload/NewResumeTemplate'

const ResumeDownload = ({ studentData, onBeforeDownload, children }) => {
  const formatResumeData = (data) => {

    const skillsMap = new Map();
    
    const addSkill = (skill) => {
        const skillName = typeof skill === 'object' ? skill?.name : skill;
        if (!skillName) return;
        
        const normalizedName = skillName.toLowerCase();
        if (!skillsMap.has(normalizedName) || 
            (skillName.match(/[A-Z]/g) || []).length > (skillsMap.get(normalizedName).match(/[A-Z]/g) || []).length) {
            skillsMap.set(normalizedName, skillName);
        }
    };

    
    data?.educationProfile?.forEach(edu => {
        edu?.skills?.forEach(skill => addSkill(skill));
    });
    
    data?.currentCourse?.skills?.forEach(skill => addSkill(skill));

    if (Array.isArray(data?.resume?.workExperience)) {
        data.resume.workExperience.forEach(exp => {
            exp?.skill_set?.forEach(skill => addSkill(skill));
        });
    }

    if (Array.isArray(data?.resume?.internships)) {
        data.resume.internships.forEach(internship => {
            internship?.skill_set?.forEach(skill => addSkill(skill));
        });
    }

    if (Array.isArray(data?.resume?.projects)) {
        data.resume.projects.forEach(project => {
            project?.skill_set?.forEach(skill => addSkill(skill));
        });
    }

    if (Array.isArray(data?.resume?.courses)) {
        data.resume.courses.forEach(course => {
            course?.skill_set?.forEach(skill => addSkill(skill));
        });
    }

    const socialMedia = data?.studentPersonalProfile?.socialMedia || [];

    const socialLinks = {
      linkedin: socialMedia.find(item => item?.link?.toLowerCase().includes('linkedin'))?.link || '',
      github: socialMedia.find(item => item?.link?.toLowerCase().includes('github'))?.link || '',
      behance: socialMedia.find(item => item?.link?.toLowerCase().includes('behance'))?.link || ''
    };


    const educationLevelOrder = {
      'phd': 1,
      'pg': 2,
      'p_g_diploma': 3,
      'puc': 4,
      'pd': 5,
      'ug': 6,
      'diploma': 7,
      'twelfth': 8,
      'tenth': 9
    };



    const cleanHTML = (html) => {
      if (!html) return '';
      return  html;
    };

    const uniqueSkills = Array.from(skillsMap.values()).sort();

    return {
      student: {
        fullName: `${data?.admin?.first_name || ''} ${data?.admin?.last_name || ''}`.trim(),
        countryCode: data?.admin?.country_code || '',
        mobile: data?.admin?.phone_number || '',
        email: data?.admin?.email || '',
        city: data?.studentPersonalProfile?.permCity || '',
        state: data?.studentPersonalProfile?.permState || '',
        linkedinUrl: socialLinks.linkedin,
        githubUrl: socialLinks.github,
        behanceUrl: socialLinks.behance,
        objective: cleanHTML(data?.resume?.objective),
        extraCurriculars: cleanHTML(data?.studentPersonalProfile?.extraCurriculars || '')
      },
      education: (() => {
        const allEducation = [...(data?.educationProfile || [])];
        
        if (data?.currentCourse && !data?.isExpCandidate) {
          allEducation.unshift({
            ...data.currentCourse,
            isCurrentCourse: true,
            institutionName: data?.instituteCampusName?.trim() || ''
          });
        }

        return allEducation
          .sort((a, b) => {
            const levelDiff = (educationLevelOrder[a.educationLevel] || 99) - (educationLevelOrder[b.educationLevel] || 99);
            if (levelDiff !== 0) return levelDiff;
            
            const endedA = parseInt(a?.endedOn) || 0;
            const endedB = parseInt(b?.endedOn) || 0;
            return endedB - endedA;
          })
          .map(edu => ({
            educationLevel: edu?.educationLevel || '',
            degree: edu?.educationLevel === 'tenth' 
              ? `SSLC - ${edu?.board}`
              : edu?.educationLevel === 'twelfth'
              ? `HSC - ${edu?.board}`
              : `${edu?.degree}${edu?.department ? ` - ${edu?.department}` : ''}${edu?.specialisation ? ` (${edu?.specialisation})` : ''}`,
            board: edu?.board || '',
            institutionName: edu?.institutionName?.trim() || '',
            city: edu?.city || '',
            startDate: edu?.startedOn > 0
              ? moment(parseInt(edu?.startedOn)).format(edu?.isCurrentCourse ? 'MMM YYYY' : 'YYYY')
              : '',
            endDate: edu?.endedOn > 0
              ? moment(parseInt(edu?.endedOn)).format(edu?.isCurrentCourse ? 'MMM YYYY' : 'YYYY')
              : '',
          }));
      })(),
      workExperience: Array.isArray(data?.resume?.workExperience)
        ? data.resume.workExperience
          .sort((a, b) => {
            if (a.isCurrentlyWorking && !b.isCurrentlyWorking) return -1;
            if (!a.isCurrentlyWorking && b.isCurrentlyWorking) return 1;
            
            const endA = a.ended_in ? moment(a.ended_in, 'MM/YYYY').valueOf() : Date.now();
            const endB = b.ended_in ? moment(b.ended_in, 'MM/YYYY').valueOf() : Date.now();
            return endB - endA;
          })
          .map(work => ({
            roleName: work?.title || '',
            companyName: work?.organization || '',
            startDate: work?.started_in ? moment(work?.started_in, 'MM/YYYY').format('MMM YYYY') : '',
            endDate: work?.ended_in ? moment(work?.ended_in, 'MM/YYYY').format('MMM YYYY') : '',
            description: cleanHTML(work?.description || ''),
            isCurrentlyWorking: work?.isCurrentlyWorking || false,
          }))
        : [],
      internships: Array.isArray(data?.resume?.internships)
        ? data.resume.internships
          .sort((a, b) => {
            const endA = a.ended_in ? moment(a.ended_in, 'MM/YYYY').valueOf() : 0;
            const endB = b.ended_in ? moment(b.ended_in, 'MM/YYYY').valueOf() : 0;
            return endB - endA;
          })
          .map(internship => ({
            roleName: internship?.role || '',
            companyName: internship?.organization || '',
            startDate: internship?.started_in ? moment(internship?.started_in, 'MM/YYYY').format('MMM YYYY') : '',
            endDate: internship?.ended_in ? moment(internship?.ended_in, 'MM/YYYY').format('MMM YYYY') : '',
            description: cleanHTML(internship?.description || ''),
            isCurrentlyWorking: internship?.isCurrentlyWorking || false,
          }))
        : [],
      projects: Array.isArray(data?.resume?.projects)
        ? data.resume.projects
          .sort((a, b) => {
            const endA = a.ended_in ? moment(a.ended_in, 'MM/YYYY').valueOf() : 0;
            const endB = b.ended_in ? moment(b.ended_in, 'MM/YYYY').valueOf() : 0;
            return endB - endA;
          })
          .map(project => ({
            title: project?.title || '',
            description: cleanHTML(project?.description || ''),
            startDate: project?.started_in ? moment(project?.started_in, 'MM/YYYY').format('MMM YYYY') : '',
            endDate: project?.ended_in ? moment(project?.ended_in, 'MM/YYYY').format('MMM YYYY') : '',
          }))
        : [],
      skills: uniqueSkills,
      certifications: (data?.resume?.courses || [])
        .sort((a, b) => {
          const yearA = parseInt(a.endYear) || 0;
          const yearB = parseInt(b.endYear) || 0;
          return yearB - yearA;
        })
        .map(course => ({
          courseName: course?.title || '',
          instituteName: course?.organization || '',
          endYear: course?.ended_in ? moment(course?.ended_in, 'MM/YYYY').format('YYYY') : '',
        })),
    };
  };

  const fileName = `${studentData?.admin?.first_name || ''} ${studentData?.admin?.last_name || ''} Resume.pdf`
    .replace(/\s+/g, ' ')
    .trim();

  return (
    <PDFDownloadLink
    key={JSON.stringify(studentData)}
      document={<NewResumeTemplate data={formatResumeData(studentData)} />}
      fileName={fileName}
    >
      {({ blob, url, loading, error }) => (
        typeof children === 'function' ? (
          children({ blob, url, loading, error, onBeforeDownload, fileName })
        ) : children ? (
          children
        ) : (
          <Button.Primary
            text={'Download Resume'}
            disabled={loading}
            onClick={onBeforeDownload}
          />
        )
      )}
    </PDFDownloadLink>
  );
};

export default ResumeDownload;
